// A minimal, permissive validation-rule shape that is structurally compatible with
// BOTH the ui/Form kit and antd's rc-field-form RuleObject (so these helpers work in
// either Form during the antd → ui migration). No antd import needed.
type Rule = {
  required?: boolean;
  message?: string;
  pattern?: RegExp;
  type?: string;
  min?: number;
  max?: number;
  len?: number;
  whitespace?: boolean;
  validator?: (...args: any[]) => any;
  transform?: (v: any) => any;
};
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * Shared Ant Design form validation rules for the Indian B2B domain
 * (mobiles, GSTIN, PAN, PIN codes, email). Keeps validation identical across
 * the retailer / farmer / user forms instead of each re-inventing patterns.
 */

// Standard 15-char GSTIN, e.g. 27ABCDE1234F1Z5.
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
// 10-char PAN, e.g. ABCDE1234F.
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const PIN_RE = /^\d{6}$/;

// A phone is valid if libphonenumber accepts it for its country. A bare number (no +) is treated as
// INDIAN (the default), so existing 10-digit entry keeps working; a number with a +country code is
// validated for that country. Country-agnostic — no per-country regex.
const validPhone = (v: any): boolean => {
  if (v == null || String(v).trim() === '') return true; // emptiness is handled by the `required` rule
  try { return isValidPhoneNumber(String(v), 'IN'); } catch { return false; }
};
const phoneRule: Rule = {
  validator: (_: any, v: any) => (validPhone(v) ? Promise.resolve() : Promise.reject(new Error('Enter a valid phone number'))),
};

export const mobileRules = (required = true): Rule[] => [
  ...(required ? [{ required: true, message: 'Mobile is required' } as Rule] : []),
  phoneRule,
];

export const optionalMobileRules: Rule[] = [phoneRule];

export const gstinRules: Rule[] = [
  { pattern: GSTIN_RE, message: 'Invalid GSTIN (e.g. 27ABCDE1234F1Z5)' },
];

export const panRules: Rule[] = [
  { pattern: PAN_RE, message: 'Invalid PAN (e.g. ABCDE1234F)' },
];

export const pincodeRules = (required = true): Rule[] => [
  ...(required ? [{ required: true, message: 'Pincode is required' } as Rule] : []),
  { pattern: PIN_RE, message: 'Enter a 6-digit PIN code' },
];

export const emailRules: Rule[] = [{ type: 'email', message: 'Enter a valid email' }];

/** Uppercase a code field as the user types (GSTIN / PAN are always upper-case). */
export const upperCase = (v?: string) => (v ? v.toUpperCase() : v);

/**
 * Display a stored E.164 number for humans: Indian numbers show as their 10-digit national form (as
 * before); other countries show the international form (e.g. `+1 408 678 6789`).
 */
export const displayMobile = (v?: string | null): string => {
  if (!v) return '';
  const pn = parsePhoneNumberFromString(String(v), 'IN');
  if (!pn) return String(v).replace(/[^\d+]/g, '');
  return pn.country === 'IN' ? String(pn.nationalNumber) : pn.formatInternational();
};

/** wa.me / WhatsApp deep-link digits — the E.164 number without the `+`, for any country. */
export const waNumber = (v?: string | null): string => {
  if (!v) return '';
  const pn = parsePhoneNumberFromString(String(v), 'IN');
  return pn ? pn.number.replace('+', '') : String(v).replace(/\D/g, '');
};

/**
 * Normalise ANY stored phone value to strict E.164 (+CC…) for the <PhoneInput> control. Legacy imports
 * left bare 10-digit Indian mobiles (`9876543210`) and country-coded numbers missing the `+`
 * (`919876543210`); handed those raw, react-phone-number-input can't parse them and renders the digits
 * verbatim — so the `+91` picker plus a raw `91…` looks like the country code twice. Coercing to E.164
 * first guarantees a clean national display. Returns undefined when nothing usable.
 */
export const toE164 = (v?: string | null): string | undefined => {
  if (v == null) return undefined;
  const raw = String(v).trim();
  if (!raw) return undefined;
  if (raw.startsWith('+')) {
    const pn = parsePhoneNumberFromString(raw);
    return pn ? pn.number : (raw.replace(/[^\d+]/g, '') || undefined);
  }
  const digits = raw.replace(/\D/g, '');
  if (!digits) return undefined;
  // Country code present but the `+` was dropped (e.g. "919876543210").
  if (digits.length > 10 && digits.startsWith('91')) {
    const pn = parsePhoneNumberFromString(`+${digits}`);
    if (pn) return pn.number;
  }
  // Bare national number → treat as Indian (the app's default country).
  const pn = parsePhoneNumberFromString(digits, 'IN');
  if (pn) return pn.number;
  if (/^[6-9]\d{9}$/.test(digits)) return `+91${digits}`;
  return `+${digits}`;
};
